import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Option, Tab, TabList } from '@fluentui/react-components';
import { DataBarVertical24Regular } from '@fluentui/react-icons';
import { ComboBox } from '@/components/common/ComboBox';
import type { ComboBoxOption } from '@/components/common/ComboBox';
import { useReportsScreenStyles } from './ReportsScreen.styles';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import type { DashboardFilterState, DateRangeOption, DashboardConfig, RoomDetail, ScheduleEvent } from '@/types/reports';
import { MOCK_DASHBOARD_CONFIG } from '@/mocks/reportsMockData';
import { KpiCard, KpiCardSkeleton, WidgetContainerSkeleton, DashboardWidgetRenderer, RoomHealthKpiCards } from '@/components/reports';
import { useGetBuildingsQuery } from '@/store/api';

const DATE_RANGE_OPTIONS: { value: DateRangeOption; labelKey: string }[] = [
  { value: 'all', labelKey: 'filters.dateRangeAll' },
  { value: 'last-1', labelKey: 'filters.dateRangeLast1' },
  { value: 'last-3', labelKey: 'filters.dateRangeLast3' },
  { value: 'last-6', labelKey: 'filters.dateRangeLast6' },
  { value: 'last-12', labelKey: 'filters.dateRangeLast12' },
];

interface ReportsScreenProps {
  config?: DashboardConfig;
}

export const ReportsScreen = ({ config = MOCK_DASHBOARD_CONFIG }: ReportsScreenProps) => {
  const styles = useReportsScreenStyles();
  const { t } = useTranslation('reports');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(config.tabs[0]?.id ?? '');
  const [filterState, setFilterState] = useState<DashboardFilterState>({
    buildingId: null,
    dateRange: 'all',
  });
  const [buildingSearch, setBuildingSearch] = useState('');

  const { data: buildingsResponse } = useGetBuildingsQuery();
  const buildings = useMemo(
    () => (buildingsResponse?.success ? buildingsResponse.data : []),
    [buildingsResponse]
  );

  const buildingOptions: ComboBoxOption[] = useMemo(
    () => {
      const allOptions = [
        { key: '', text: t('filters.allBuildings') },
        ...buildings.map((b) => ({ key: b.id, text: b.buildingName })),
      ];
      if (!buildingSearch) return allOptions;
      return allOptions.filter((o) =>
        o.text.toLowerCase().includes(buildingSearch.toLowerCase())
      );
    },
    [buildings, t, buildingSearch]
  );

  useEffect(() => {
    telemetry.trackPageView(TELEMETRY_COMPONENT, 'ReportAnalyticsDashboard');
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleBuildingChange = (buildingId: string | null) => {
    setFilterState((prev) => ({ ...prev, buildingId }));
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.UI_INTERACTION, {
      action: 'BuildingFilterChange',
      value: buildingId ?? 'all',
    });
  };

  const handleDateRangeChange = (dateRange: DateRangeOption) => {
    setFilterState((prev) => ({ ...prev, dateRange }));
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.UI_INTERACTION, {
      action: 'DateRangeFilterChange',
      value: dateRange,
    });
  };

  const activeTabConfig = config.tabs.find((tab) => tab.id === activeTab);
  const tabKpis = useMemo(
    () => (activeTabConfig?.kpiIds ?? []).map((id) => config.kpis.find((k) => k.id === id)).filter(Boolean),
    [activeTabConfig, config.kpis]
  );
  const widgetGroups = useMemo(() => {
    const widgets = activeTabConfig?.widgets ?? [];
    const groups: { layout: 'full' | 'half'; items: typeof widgets }[] = [];
    for (const w of widgets) {
      const last = groups[groups.length - 1];
      if (last && last.layout === w.layout) {
        last.items.push(w);
      } else {
        groups.push({ layout: w.layout, items: [w] });
      }
    }
    return groups;
  }, [activeTabConfig]);
  const hasContent = tabKpis.length > 0 || (activeTabConfig?.widgets.length ?? 0) > 0;

  return (
    <div className={styles.root}>
      <header className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <DataBarVertical24Regular className={styles.topBarIcon} />
          <span className={styles.topBarTitle}>{t('dashboard.headerTitle')}</span>
          <span className={styles.topBarSubtitle}>{t('dashboard.headerSubtitle')}</span>
        </div>
        <ComboBox
          className={styles.buildingDropdown}
          placeholder={t('filters.buildingPlaceholder')}
          aria-label={t('filters.buildingLabel')}
          options={buildingOptions}
          selectedOptions={[filterState.buildingId ?? '']}
          onChange={(e) => setBuildingSearch((e.target as HTMLInputElement).value)}
          onOptionSelect={(_, data) => {
            handleBuildingChange(data.optionValue === '' ? null : (data.optionValue as string));
            setBuildingSearch('');
          }}
        />
      </header>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as string)}
        className={styles.tabList}
      >
        {config.tabs.map((tab) => (
          <Tab key={tab.id} value={tab.id} className={styles.tab}>
            <div>
              <div className={styles.tabLabel}>{t(tab.labelKey)}</div>
              <div className={styles.tabSubtitle}>{t(tab.subtitleKey)}</div>
            </div>
          </Tab>
        ))}
      </TabList>

      <div className={styles.sectionRow}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{t(activeTabConfig?.labelKey ?? '')}</span>
          <span className={styles.sectionSeparator}>—</span>
          <span className={styles.sectionSubtitle}>{t(activeTabConfig?.subtitleKey ?? '')}</span>
        </div>
        {activeTab !== 'run-of-show' && (
          <Dropdown
            className={styles.dateRangeDropdown}
            aria-label={t('filters.dateRangeLabel')}
            value={t(DATE_RANGE_OPTIONS.find((o) => o.value === filterState.dateRange)!.labelKey)}
            onOptionSelect={(_, data) => handleDateRangeChange(data.optionValue as DateRangeOption)}
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </Option>
            ))}
          </Dropdown>
        )}
      </div>

      {hasContent ? (
        <>
          {tabKpis.length > 0 && (
            activeTabConfig?.kpiRenderer === 'room-health' ? (
              isLoading
                ? <div className={styles.kpiRow}>{Array.from({ length: tabKpis.length }, (_, i) => <KpiCardSkeleton key={i} />)}</div>
                : <RoomHealthKpiCards data={(config.datasets['roomDetails'] ?? []) as RoomDetail[]} />
            ) : (
              <div className={styles.kpiRow}>
                {isLoading
                  ? Array.from({ length: tabKpis.length }, (_, i) => <KpiCardSkeleton key={i} />)
                  : tabKpis.map((metric) => <KpiCard key={metric!.id} metric={metric!} />)}
              </div>
            )
          )}

          {widgetGroups.map((group, gi) =>
            group.layout === 'full' ? (
              group.items.map((widget) => (
                <div key={widget.id} className={styles.widgetFullWidth}>
                  {isLoading ? (
                    <WidgetContainerSkeleton />
                  ) : (
                    <DashboardWidgetRenderer
                      widget={widget}
                      data={config.datasets[widget.dataKey]}
                      purposes={config.purposes}
                      roomTypes={config.roomTypes}
                      scheduleEvents={config.datasets['scheduleEvents'] as ScheduleEvent[]}
                      largeEventThreshold={config.largeEventThreshold}
                    />
                  )}
                </div>
              ))
            ) : (
              <div key={`half-group-${gi}`} className={styles.widgetRow}>
                {isLoading
                  ? group.items.map((w) => <WidgetContainerSkeleton key={w.id} />)
                  : group.items.map((widget) => (
                      <DashboardWidgetRenderer
                        key={widget.id}
                        widget={widget}
                        data={config.datasets[widget.dataKey]}
                        purposes={config.purposes}
                        roomTypes={config.roomTypes}
                        scheduleEvents={config.datasets['scheduleEvents'] as ScheduleEvent[]}
                        largeEventThreshold={config.largeEventThreshold}
                      />
                    ))}
              </div>
            )
          )}
        </>
      ) : (
        <div className={styles.comingSoon}>
          <p className={styles.comingSoonText}>{t('emptyState.noData')}</p>
        </div>
      )}
    </div>
  );
};

ReportsScreen.displayName = 'ReportsScreen';
